import React, { Component } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import lodashDebounce from 'lodash/debounce';
import { Button, Loader, TextFieldCore } from '@adsk/bim360-matrix-react-components';
import { connect } from '../redux/connect';

import {
  validateRfiCustomIdentifierAction,
  getRfiCustomIdentifierAction,
  resetRfiCustomIdentifierValidityAction,
  updateRfiCustomIdentifierAnalyticsIsEditedAction,
  updateRfiCustomIdentifierAnalyticsNextSuggestionClickedAction,
} from '../redux/actions';
import {
  isFetchingRfiCustomIdentifier,
  getSuggestedNextRfiCustomIdentifier,
  getIsRfiCustomIdentifierValid,
  getShowValidationErrorMessage,
  getShowValidationErrorMessageLink,
  isValidatingRfiCustomIdentifier,
  getRfiCustomIdentifierAnalyticsDataIsEdited,
} from '../redux/selectors';
import {
  rfiCustomIdentifierAllowedCharactersValidator,
  rfiCustomIdentifierMaxLengthValidator,
  presenceValidator,
} from '../utils/validation-helpers';
import FormField from './FormField';
import { CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES } from '../constants/custom-identifier';

/**
 * RfiCustomIdentifier
 * This component is basically a text input that has the following types of value validations:
 * 1. Client validations: allowed characters, max length and required field.
 * 2. BE validation
 */
class RfiCustomIdentifier extends Component {
  constructor(props) {
    super(props);

    this.initialClientValidation = {
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS]: true,
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH]: true,
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED]: true,
    };

    this.clientValidationErrorMessagesMapping = {
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS]: T.translate(
        'new.rfiCustomIdentifierAllowedCharactersValidationMessage'
      ),
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH]: T.translate(
        'new.rfiCustomIdentifierMaxCharactersValidationMessage'
      ),
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED]: T.translate(
        'new.rfiCustomIdentifierFieldRequired'
      ),
    };

    this.state = {
      // Indicates if the validation type is valid/invalid
      clientValidation: this.initialClientValidation,
      isValuePristine: true,
    };

    this.validateCustomIdentifier = this.validateCustomIdentifier.bind(this);
    this.onChangeText = this.onChangeText.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.getCustomIdentifier = this.getCustomIdentifier.bind(this);
    this.updateClientValidationState = this.updateClientValidationState.bind(this);
    this.isFieldValid = this.isFieldValid.bind(this);
    this.shouldShowErrorMessageLink = this.shouldShowErrorMessageLink.bind(this);
    this.getErrorMessage = this.getErrorMessage.bind(this);
    this.resetClientValidationState = this.resetClientValidationState.bind(this);
    this.onGetRfiCustomIdentifierSuccess = this.onGetRfiCustomIdentifierSuccess.bind(this);
    this.onValidateRfiCustomIdentifierSuccess = this.onValidateRfiCustomIdentifierSuccess.bind(
      this
    );

    this.validateCustomIdentifierDebounced = lodashDebounce(this.validateCustomIdentifier, 2000);
  }

  componentDidMount() {
    const {
      populateRfiCustomIdentifierOnMount,
      getRfiCustomIdentifier,
      value,
      onValidationChange,
      isRfiCustomIdentifierValid,
      rfiId,
      isRevision,
    } = this.props;
    // fetch value from BE or update validations on existing value
    if (populateRfiCustomIdentifierOnMount) {
      getRfiCustomIdentifier({
        rfiId,
        isRevision,
        onSuccess: this.onGetRfiCustomIdentifierSuccess
      });
    } else {
      const isClientValid = this.updateClientValidationState(value);
      onValidationChange(isClientValid && isRfiCustomIdentifierValid);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { forceClientValidation, onValidationChange } = this.props;
    if (
      forceClientValidation !== nextProps.forceClientValidation &&
      nextProps.forceClientValidation
    ) {
      this.updateClientValidationState(nextProps.value);
      onValidationChange(false);
    }
  }

  /**
   * Handle getRfiCustomIdentifierAction on success
   * @param suggestedNextRfiCustomIdentifier
   */
  onGetRfiCustomIdentifierSuccess(suggestedNextRfiCustomIdentifier) {
    const { onChange, onValidationChange } = this.props;
    if (suggestedNextRfiCustomIdentifier) {
      onChange(suggestedNextRfiCustomIdentifier);
      onValidationChange(true);
      this.resetClientValidationState();
    } else {
      // in case BE returned empty custom identifier
      // we should make the form invalid to prevent form create click
      onValidationChange(false);
    }
  }

  /**
   * Handle validateRfiCustomIdentifierAction on success
   * @param isValid
   */
  onValidateRfiCustomIdentifierSuccess(isValid) {
    const { onValidationChange } = this.props;
    onValidationChange(isValid);
  }

  /**
   * Handles text change event
   * @param value
   */
  onChangeText(value) {
    const {
      serverValue,
      onChange,
      onValidationChange,
      rfiCustomIdentifierAnalyticsDataIsEdited,
      updateRfiCustomIdentifierAnalyticsIsEdited,
    } = this.props;

    const isValuePristine = serverValue && value === serverValue;
    const isClientValid = this.updateClientValidationState(value);
    this.setState({ isValuePristine });

    if (isValuePristine) {
      this.validateCustomIdentifierDebounced.cancel();
      onChange(value);
      onValidationChange(true);
      return;
    }

    onChange(value);
    // Even if the client validation is valid we should make the form invalid until BE returned a value
    // We do this to prevent form submission until BE returns with validation response
    onValidationChange(false);

    // mark this fields as edited for analytics purposes
    if (!rfiCustomIdentifierAnalyticsDataIsEdited) {
      updateRfiCustomIdentifierAnalyticsIsEdited();
    }

    if (value && isClientValid) {
      // Start timeout for BE validation of the value
      this.validateCustomIdentifierDebounced(value);
    } else {
      // Cancel timeout to BE validation of the value
      this.validateCustomIdentifierDebounced.cancel();
    }
  }

  /**
   * If Waiting for BE validation timeout - execute BE validation now
   */
  onBlur() {
    this.validateCustomIdentifierDebounced.flush();
  }

  getShowValidationErrorMessage() {
    const { showValidationErrorMessage } = this.props;
    const { isValuePristine } = this.state;

    return showValidationErrorMessage && !isValuePristine;
  }

  /**
   * Fetch custom identifier from BE if suggestedNextRfiCustomIdentifier does not exist
   */
  getCustomIdentifier() {
    const {
      suggestedNextRfiCustomIdentifier,
      resetRfiCustomIdentifierValidity,
      onChange,
      onValidationChange,
      getRfiCustomIdentifier,
      updateRfiCustomIdentifierAnalyticsNextSuggestionClicked,
      rfiId,
      isRevision,
    } = this.props;

    // if suggestedNextRfiCustomIdentifier exists:
    // update the text input with this value and reset validation properties in the reducer since now we have a valid value
    if (suggestedNextRfiCustomIdentifier) {
      resetRfiCustomIdentifierValidity();
      this.resetClientValidationState();
      onChange(suggestedNextRfiCustomIdentifier);
      onValidationChange(true);
    } else {
      getRfiCustomIdentifier({
        rfiId,
        isRevision,
        onSuccess: this.onGetRfiCustomIdentifierSuccess
      });
    }
    updateRfiCustomIdentifierAnalyticsNextSuggestionClicked();
  }

  /**
   * Returns the error message according to the validation type
   * @returns {*}
   */
  getErrorMessage() {
    const { clientValidation } = this.state;
    if (!clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH]) {
      return this.clientValidationErrorMessagesMapping[
        CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH
      ];
    }
    if (!clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS]) {
      return this.clientValidationErrorMessagesMapping[
        CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS
      ];
    }
    if (!clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED]) {
      return this.clientValidationErrorMessagesMapping[
        CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED
      ];
    }
    if (this.getShowValidationErrorMessage()) {
      return T.translate('new.error.customIdentifierNotAvailable');
    }
    return null;
  }

  /**
   * Returns true if the value is valid in the client and BE, false other wise
   * The value is true if all client validations are valid and when BE validation resulted in valid response
   * @returns {*|boolean}
   */
  isFieldValid() {
    const { clientValidation } = this.state;
    return (
      clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH] &&
      clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS] &&
      clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED] &&
      !this.getShowValidationErrorMessage()
    );
  }

  /**
   * Returns true if error message link should be visible, false other wise.
   * error message link should be visible when:
   * 1. RFI custom identifier entered by the user is invalid with BE and the BE returned a suggestion for the next number
   * 2. RFI custom identifier is missing (required)
   * @returns {*|boolean|Boolean}
   */
  shouldShowErrorMessageLink() {
    const { showValidationErrorMessageLink } = this.props;
    const { clientValidation } = this.state;
    return (
      clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH] &&
      clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS] &&
      (!clientValidation[CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED] ||
        (this.getShowValidationErrorMessage() && showValidationErrorMessageLink))
    );
  }

  /**
   * Execute all client validations on the trimmed value and update client validation state.
   * @param value
   * @returns {boolean} The client validation state of all validations types
   */
  updateClientValidationState(value) {
    const { resetRfiCustomIdentifierValidity } = this.props;

    const trimmedValue = value && value.trim();
    const allowedCharacters = rfiCustomIdentifierAllowedCharactersValidator(trimmedValue);
    const maxLength = rfiCustomIdentifierMaxLengthValidator(trimmedValue, 15);
    const required = presenceValidator(trimmedValue);

    const clientValidation = {
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.ALLOWED_CHARACTERS]: allowedCharacters,
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.MAX_LENGTH]: maxLength,
      [CUSTOM_IDENTIFIER_CLIENT_VALIDATION_TYPES.REQUIRED]: !!required,
    };
    this.setState({ clientValidation });

    // Check if all validations types are valid
    const isValid = !!required && maxLength && allowedCharacters;

    // If one of the client validation types is invalid we need to reset the validation properties of the reducer
    // The reason we do it, is for the edge case of when user types an invalid BE value, gets BE validation error message,
    // then types an invalid client value, gets a client validation error message, then if he changes the value to a client valid value,
    // the BE validation error message will appear if we wont reset the validation properties of the reducer.
    // On the other hand, if one of the client validation types is valid and the BE validation is invalid,
    // we don't need to reset the validation properties of the reducer since BE validation process will start and these properties
    // will be updated and the UX design asked to leave the BE validation error message visible until validation will finish.
    if (!isValid) {
      resetRfiCustomIdentifierValidity();
    }
    return isValid;
  }

  /**
   * Reset all client validations state to valid
   */
  resetClientValidationState() {
    this.setState({ clientValidation: this.initialClientValidation });
  }

  /**
   * Validate custom identifier with BE, send trimmed value
   * @param value
   */
  validateCustomIdentifier(value) {
    const { validateRfiCustomIdentifier } = this.props;
    validateRfiCustomIdentifier(
      { rfiCustomIdentifier: value.trim() },
      { onSuccess: this.onValidateRfiCustomIdentifierSuccess }
    );
  }

  /**
   * Renders the validation error message according to the validation type
   * @returns {*}
   */
  renderErrorMessage() {
    const isFieldValid = this.isFieldValid();
    if (isFieldValid) {
      return null;
    }

    const shouldShowErrorMessageLink = this.shouldShowErrorMessageLink();

    return (
      <div
        className="RfiCustomIdentifier__validation-message-container"
        data-testid="RfiCustomIdentifier__validation-message-container"
      >
        {this.getErrorMessage()}
        {shouldShowErrorMessageLink && (
          <div className="RfiCustomIdentifier__validation-message-separator">-</div>
        )}
        {shouldShowErrorMessageLink && (
          <Button
            className="RfiCustomIdentifier__get-custom-identifier"
            data-testid="RfiCustomIdentifier__get-custom-identifier"
            styleType={Button.StyleType.INLINE_LINK}
            onClick={this.getCustomIdentifier}
          >
            {T.translate('new.getNextCustomIdentifier')}
          </Button>
        )}
      </div>
    );
  }

  render() {
    const {
      className,
      value,
      disabled,
      readOnly,
      isFetching,
      isValidating,
      container,
      setFieldRef,
    } = this.props;

    return (
      <div className="RfiCustomIdentifier" ref={setFieldRef}>
        <FormField
          labelText={T.translate('new.number')}
          infoPopoverText={T.translate('info.rfiCustomIdentifier')}
          container={container}
          isFieldMandatory
          errorMessageText={this.renderErrorMessage()}
          showFieldError={!this.isFieldValid()}
        >
          <div className="RfiCustomIdentifier__text-field-container">
            <TextFieldCore
              data-testid="RfiCustomIdentifier__rfi-custom-identifier"
              valid={this.isFieldValid()}
              behavior={TextFieldCore.Behaviors.SINGLE_LINE}
              value={value}
              className={className}
              onChange={this.onChangeText}
              onBlur={this.onBlur}
              disabled={disabled}
              readOnly={readOnly}
            />
            {isFetching || isValidating ? <Loader className="RfiCustomIdentifier__loader" /> : null}
          </div>
        </FormField>
      </div>
    );
  }
}

RfiCustomIdentifier.displayName = 'RfiCustomIdentifier';
RfiCustomIdentifier.defaultProps = {
  className: undefined,
  value: undefined,
  serverValue: undefined,
  disabled: false,
  isFetching: false,
  isValidating: false,
  suggestedNextRfiCustomIdentifier: undefined,
  forceClientValidation: false,
  populateRfiCustomIdentifierOnMount: false,
  setFieldRef: undefined,
};
RfiCustomIdentifier.propTypes = {
  /**
   * className
   */
  className: PropTypes.string,
  /**
   * The value of the text input
   */
  value: PropTypes.string,
  /**
   * The current value of customIdentifier field on server
   */
  serverValue: PropTypes.string,
  /**
   * A callback function to execute when the value changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * A callback function to execute when the validation changed
   */
  onValidationChange: PropTypes.func.isRequired,
  /**
   * disable the component
   */
  disabled: PropTypes.bool,
  /**
   * validate the value entered by the user
   */
  validateRfiCustomIdentifier: PropTypes.func.isRequired,
  /**
   * Fetch the suggestedNextRfiCustomIdentifier
   */
  getRfiCustomIdentifier: PropTypes.func.isRequired,
  /**
   * Indicates if the client is waiting for the value to be fetched from the BE
   */
  isFetching: PropTypes.bool,
  /**
   * Indicates when if client is waiting for the value to be validated in the BE
   */
  isValidating: PropTypes.bool,
  /**
   * The suggested next RFI custom identifier returned from the BE.
   * This value can be returned from the BE when calling fetch or validate requests
   */
  suggestedNextRfiCustomIdentifier: PropTypes.string,
  /**
   * Indicates if the value entered by the user is valid with BE.
   */
  isRfiCustomIdentifierValid: PropTypes.bool.isRequired,
  /**
   * Indicates if the component should show a BE validation error message.
   */
  showValidationErrorMessage: PropTypes.bool.isRequired,
  /**
   * Indicates if the component should show a BE validation error message link.
   */
  showValidationErrorMessageLink: PropTypes.bool.isRequired,
  /**
   * Reset action to reset some of the reducer validation properties.
   */
  resetRfiCustomIdentifierValidity: PropTypes.func.isRequired,
  /**
   * Force client validation execution.
   * This was added for an edge case where the field is empty and user clicks on submit form from outside.
   */
  forceClientValidation: PropTypes.bool,
  /**
   * Indicates if the component should fetch the value from the BE on mount
   * For example - this can be set to true when create form is mounted, but set to false
   * when user select draft status and then open status which causes remount
   */
  populateRfiCustomIdentifierOnMount: PropTypes.bool,
  /**
   * container of the component
   */
  // eslint-disable-next-line react/forbid-prop-types
  container: PropTypes.object.isRequired,
  /**
   * Update analytics data
   */
  updateRfiCustomIdentifierAnalyticsIsEdited: PropTypes.func.isRequired,
  /**
   * Update analytics data
   */
  updateRfiCustomIdentifierAnalyticsNextSuggestionClicked: PropTypes.func.isRequired,
  /**
   * Inticated whether the field was edited - this data is collected for analytics purposes
   */
  rfiCustomIdentifierAnalyticsDataIsEdited: PropTypes.bool.isRequired,
  /**
   * Sets a reference to the element for scrolling perposes when form error occured
   */
  setFieldRef: PropTypes.func,
  /**
   * Indicates whether the component is for revision
   */
  isRevision: PropTypes.bool.isRequired,
  /**
   * Rfi Id of the issue
   */
  rfiId: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => ({
  isFetching: isFetchingRfiCustomIdentifier(state),
  isValidating: isValidatingRfiCustomIdentifier(state),
  suggestedNextRfiCustomIdentifier: getSuggestedNextRfiCustomIdentifier(state),
  isRfiCustomIdentifierValid: getIsRfiCustomIdentifierValid(state),
  showValidationErrorMessage: getShowValidationErrorMessage(state),
  showValidationErrorMessageLink: getShowValidationErrorMessageLink(state),
  rfiCustomIdentifierAnalyticsDataIsEdited: getRfiCustomIdentifierAnalyticsDataIsEdited(state),
});

const mapDispatchToProps = {
  validateRfiCustomIdentifier: validateRfiCustomIdentifierAction,
  getRfiCustomIdentifier: getRfiCustomIdentifierAction,
  resetRfiCustomIdentifierValidity: resetRfiCustomIdentifierValidityAction,
  updateRfiCustomIdentifierAnalyticsIsEdited: updateRfiCustomIdentifierAnalyticsIsEditedAction,
  updateRfiCustomIdentifierAnalyticsNextSuggestionClicked: updateRfiCustomIdentifierAnalyticsNextSuggestionClickedAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(RfiCustomIdentifier);
